/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Badge, BadgeVariety, Heading, Spinner, toast } from '@sonarsource/echoes-react';
import { noop } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { isDefined, isStringDefined } from '~shared/helpers/types';
import useLocalStorage from '~shared/helpers/useLocalStorage';
import { getAtlassianAuthUrl } from '../../api/jira';
import {
  useJiraOrganizationBindingQuery,
  usePatchJiraBindingMutation,
  usePostJiraBindingMutation,
} from '../../queries';
import { AtlassianAccessibleResource } from '../../types';
import { OrganizationJiraConnected } from './OrganizationJiraConnected';
import { OrganizationJiraNotConnected } from './OrganizationJiraNotConnected';
import { OrganizationJiraPatchModal } from './OrganizationJiraPatchModal';

interface OrganizationJiraBindingProps {
  isCustomJiraApp?: boolean;
  isFormDiscreet?: boolean;
  organizationKey: string;
  organizationName?: string;
  organizationUuid: string;
  withProjectPermissionsSwitch?: boolean;
}

export function OrganizationJiraBinding({
  isCustomJiraApp = false,
  isFormDiscreet = false,
  organizationKey,
  organizationName,
  organizationUuid,
  withProjectPermissionsSwitch,
}: Readonly<OrganizationJiraBindingProps>) {
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: jiraBinding, isLoading: isLoadingBinding } = useJiraOrganizationBindingQuery(
    organizationUuid,
    { enabled: organizationUuid !== undefined },
  );

  const isBound = isDefined(jiraBinding);
  const [isConnectPressed, setIsConnectPressed] = useState(false);
  const [wasRedirectHandled, setWasRedirectHandled] = useState(false);
  const [isPatchModalOpen, setIsPatchModalOpen] = useState(false);
  const [isCalloutShown, setIsCalloutShown] = useLocalStorage(
    `jira-org-binding-callout_${organizationKey}`,
    false,
  );
  const [resources, setResources] = useState<AtlassianAccessibleResource[]>([]);

  const { mutateAsync: postBinding, isPending: isPostingBinding } = usePostJiraBindingMutation();
  const { mutateAsync: patchBinding, isPending: isPatchingBinding } = usePatchJiraBindingMutation();

  const onConnect = useCallback(() => {
    setIsConnectPressed(true);
    // eslint-disable-next-line local-rules/no-api-imports -- It's a fire-and-forget call. Doesn't make sense to use a query for this.
    getAtlassianAuthUrl(organizationKey, organizationUuid ?? '')
      .then((atlassianAuthUrl) => {
        if (isStringDefined(atlassianAuthUrl)) {
          globalThis.location.href = atlassianAuthUrl;
        }
      })
      .catch(() => {
        setIsConnectPressed(false);
      });
  }, [organizationKey, organizationUuid]);

  const handleJiraInstanceSelection = useCallback(
    (selected: AtlassianAccessibleResource) => {
      patchBinding({
        sonarOrganizationUuid: organizationUuid,
        jiraCloudId: selected.cloudId,
        jiraInstanceUrl: selected.url,
      })
        .then(({ id }) => {
          showBindingCreatedToast();
          // Show the callout only if a new binding was created
          setIsCalloutShown(id !== jiraBinding?.id);
        })
        .catch(noop)
        .finally(() => {
          setIsPatchModalOpen(false);
        });
    },
    [organizationUuid, patchBinding, setIsCalloutShown, jiraBinding],
  );

  useEffect(() => {
    const state = searchParams.get('state');
    const authorizationCode = searchParams.get('code');

    if (
      !wasRedirectHandled &&
      isStringDefined(state) &&
      isStringDefined(authorizationCode) &&
      !isLoadingBinding
    ) {
      setWasRedirectHandled(true); // only post ONCE for the first page load after the redirect
      postBinding({ authorizationCode, state })
        .then((response) => {
          if (response.binding) {
            showBindingCreatedToast();
            // Show the callout only if a new binding was created
            setIsCalloutShown(response.binding.id !== jiraBinding?.id);
          } else {
            setResources(response.resources);
            setIsPatchModalOpen(true);
          }
        })
        .catch(noop)
        .finally(() => {
          // Clear the URL parameters whatever the request's response
          searchParams.delete('state');
          searchParams.delete('code');
          setSearchParams(searchParams);
        });
    }
  }, [
    wasRedirectHandled,
    postBinding,
    searchParams,
    setSearchParams,
    setIsCalloutShown,
    isLoadingBinding,
    jiraBinding,
  ]);

  const isLoading = isLoadingBinding;

  const badgeText = isBound
    ? formatMessage({ id: 'organization.jira.binding.badge.connected' })
    : formatMessage({ id: 'organization.jira.binding.badge.not_connected' });

  const badgeVariety = isBound ? BadgeVariety.Success : BadgeVariety.Neutral;

  return (
    <div>
      <div className="sw-flex sw-items-center sw-gap-2 sw-mb-4">
        <Heading as="h1">
          <FormattedMessage id="organization.jira.binding.header" />
        </Heading>
        {!isLoading && (
          <Badge className="sw-ml-2" variety={badgeVariety}>
            {badgeText}
          </Badge>
        )}
      </div>
      <Spinner isLoading={isLoading}>
        {isBound ? (
          <OrganizationJiraConnected
            binding={jiraBinding}
            isCalloutShown={Boolean(isCalloutShown)}
            isCustomJiraApp={isCustomJiraApp}
            isPending={isConnectPressed}
            onReauthorize={onConnect}
            organizationKey={organizationKey}
            setIsCalloutShown={setIsCalloutShown}
            sonarOrganizationName={organizationName}
            withProjectPermissionsSwitch={withProjectPermissionsSwitch}
          />
        ) : (
          <OrganizationJiraNotConnected
            isCustomJiraApp={isCustomJiraApp}
            isFormDiscreet={isFormDiscreet}
            isPending={isPostingBinding || isConnectPressed}
            onConnect={onConnect}
            sonarOrganizationName={organizationName}
          />
        )}
      </Spinner>
      <OrganizationJiraPatchModal
        isOpen={isPatchModalOpen}
        isPatchingBinding={isPatchingBinding}
        onCancel={() => {
          setIsPatchModalOpen(false);
        }}
        onConfirm={handleJiraInstanceSelection}
        resources={resources}
      />
    </div>
  );
}

function showBindingCreatedToast() {
  toast.success({
    isDismissable: true,
    description: <FormattedMessage id="organization.jira.binding.bind_success" />,
    duration: 'medium',
  });
}
